import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import { apiFetch } from '@/utils/api'
import { enableStoreDevtools } from '@/config/runtimeFlags'

let reportsAbortController: AbortController | null = null
let scansAbortController: AbortController | null = null

const isAbortError = (error: unknown): boolean => {
  if (error instanceof DOMException) {
    return error.name === 'AbortError'
  }
  if (error instanceof Error) {
    return error.name === 'AbortError'
  }
  return false
}

const abortInflightDataFetches = () => {
  if (reportsAbortController) {
    reportsAbortController.abort()
    reportsAbortController = null
  }
  if (scansAbortController) {
    scansAbortController.abort()
    scansAbortController = null
  }
}

const TELEMETRY_CONSENT_STORAGE_KEY = 'cybersec:telemetry-consent'

export type TelemetryConsentState = 'unknown' | 'granted' | 'denied'

const readTelemetryConsent = (): TelemetryConsentState => {
  if (typeof window === 'undefined') return 'unknown'
  try {
    const stored = localStorage.getItem(TELEMETRY_CONSENT_STORAGE_KEY)
    return stored === 'granted' || stored === 'denied' ? stored : 'unknown'
  } catch {
    return 'unknown'
  }
}

const persistTelemetryConsent = (value: TelemetryConsentState) => {
  if (typeof window === 'undefined') return
  try {
    if (value === 'unknown') {
      localStorage.removeItem(TELEMETRY_CONSENT_STORAGE_KEY)
    } else {
      localStorage.setItem(TELEMETRY_CONSENT_STORAGE_KEY, value)
    }
  } catch {
    // Ignore storage failures silently to avoid disrupting UX
  }
}

// Types
export interface Vulnerability {
  id: string
  type: string
  severity: 'Critical' | 'High' | 'Medium' | 'Low'
  cvss: number
  description: string
  impact: string
  remediation: string[]
  evidence: Array<{
    line: number
    content: string
  }>
  discoveredAt: string
}

export interface Report {
  id: string
  scanId: string
  title: string
  target: string
  command: string
  vulnerabilities: {
    total: number
    critical: number
    high: number
    medium: number
    low: number
    riskScore: number
    riskLevel: string
    findings: Vulnerability[]
  }
  extractedData: {
    databases: string[]
    tables: string[]
    columns: string[]
    users: string[]
    systemInfo: Record<string, string>
  }
  recommendations: Array<{
    category: string
    priority: string
    title: string
    description: string
    implementation: string[]
    effort: string
    impact: string
  }>
  scanDuration: number
  status: string
  metadata: {
    generatedAt: string
    scanProfile: string
    reportVersion: string
    scanner: string
  }
}

export interface ReportNotification {
  id: string
  scanId: string
  reportId: string
  target?: string
  status: 'completed' | 'failed'
  createdAt: string
  exitCode?: number | null
}

export type ScanVerdictOutcome = 'vulnerable' | 'clean' | 'suspected' | 'error' | 'unknown'

export interface ScanVerdictTimelineEvent {
  type: string
  detail?: string
  parameter?: string
  place?: string
  at?: string | null
}

export interface ScanVerdictParameterStatus {
  name: string
  place: string
  finalStatus: 'confirmed' | 'dismissed' | 'suspected' | 'testing' | 'unknown'
  statuses: Array<{
    status: string
    detail?: string
    at?: string | null
    source?: string
  }>
  techniques: string[]
  payloads: string[]
  confidence: number | null
  heuristics: {
    flagged: boolean
    dismissed: boolean
  }
  notes: string[]
  metrics?: {
    confirmedFindings?: number
  }
}

export interface ScanVerdictMeta {
  sessionId?: string | null
  generatedAt?: string
  parameters: ScanVerdictParameterStatus[]
  heuristics: {
    flaggedParameters: string[]
    dismissedParameters: string[]
  }
  timeline: ScanVerdictTimelineEvent[]
  summary: {
    outcome: ScanVerdictOutcome
    reason?: string | null
    rawVerdictLine?: string | null
    evidenceCount?: number
    exitCode?: number
    completedAt?: string
  }
  stats: {
    confirmed: number
    suspected: number
    dismissed: number
    totalTested: number
    confirmedFindings?: number
    confirmedParameters?: number
  }
}

export type ScanPolicyState = {
  allowArbitraryVerification: boolean
  enforcePreapprovedTargets: boolean
  disableNonAdminScans: boolean
  updatedAt: string | null
  updatedBy: string | null
}

export interface Scan {
  id: string
  target: string
  options: Record<string, any>
  scanProfile: string
  status: 'pending' | 'running' | 'completed' | 'failed' | 'terminated'
  startTime: string
  endTime?: string
  exitCode?: number
  error?: string
  output: string
  createdAt: string
  updatedAt: string
  verdictMeta?: ScanVerdictMeta | null
  preScanMeta?: Record<string, any> | null
}

export interface ScanProfile {
  id: string
  name: string
  description: string
  flags: string[]
  baseMode?: 'full' | 'minimal'
}

export interface Settings {
  theme: 'dark' | 'light' | 'auto'
  terminalFontSize: number
  terminalTheme: string
  notifications: boolean
  autoSave: boolean
  maxConcurrentScans: number
  defaultScanProfile: string
  apiTimeout: number
}

export type TerminalAuthType = 'none' | 'cookie' | 'login'

export interface TerminalFormState {
  targetUrl: string
  authType: TerminalAuthType
  authCookie: string
  authHeaderName: string
  authHeaderValue: string
  loginUrl: string
  loginUsername: string
  loginPassword: string
  loginUsernameField: string
  loginPasswordField: string
  csrfRegex: string
  csrfFieldName: string
  csrfHeaderName: string
  skipQueue: boolean  // User choice: queue (precision) vs direct (instant)
}

export type LastScanStatus = 'idle' | 'pending' | 'running' | 'completed' | 'failed' | 'terminated'

export interface LastScanContext {
  scanId: string | null
  target: string
  profile: string
  options: Record<string, any>
  status: LastScanStatus
  startedAt: string | null
  completedAt: string | null
  exitCode: number | null
  sessionId: string | null
  error: string | null
}

export interface AppState {
  // Connection state
  isConnected: boolean
  connectionError: string | null
  // Auth
  authToken: string | null
  currentUser: { id: string; email: string; role: string } | null
  isAuthenticated: boolean
  telemetryConsent: TelemetryConsentState

  // Application state
  isLoading: boolean

  // Governance
  scanPolicy: ScanPolicyState | null

  // Scans
  scans: Scan[]
  currentScan: Scan | null
  scansTotal: number
  scanProfiles: ScanProfile[]

  // Reports
  reports: Report[]
  reportsTotal: number
  reportsLimit: number
  reportsOffset: number
  reportsQuery: string
  currentReport: Report | null

  // Settings
  settings: Settings

  // Terminal
  terminalHistory: string[]
  terminalOutput: string

  // UI state
  sidebarCollapsed: boolean
  activeTerminalSession: string | null
  // Terminal preferences
  terminalSelectedProfile: string
  terminalCustomFlags: string
  lastCustomFlagsByUser: Record<string, string>
  terminalForm: TerminalFormState
  lastScanContext: LastScanContext

  // Report notifications
  reportNotifications: ReportNotification[]
}

interface AppActions {
  // Initialization
  initialize: () => Promise<void>
  // Auth
  setAuthToken: (token: string | null) => void
  setTelemetryConsent: (consent: TelemetryConsentState) => void
  fetchMe: (opts?: { suppressUnauthorizedEvent?: boolean }) => Promise<void>
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string) => Promise<void>
  logout: () => void

  // Connection management
  setConnected: (connected: boolean) => void
  setConnectionError: (error: string | null) => void

  // Scan management
  addScan: (scan: Scan) => void
  updateScan: (scanId: string, updates: Partial<Scan>) => void
  removeScan: (scanId: string) => void
  setCurrentScan: (scan: Scan | null) => void
  loadScans: () => Promise<void>
  loadRunningScans: () => Promise<void>
  upsertScanFromEvent: (scan: Partial<Scan> & { id: string }) => void

  // Report management
  addReport: (report: Report) => void
  updateReport: (reportId: string, updates: Partial<Report>) => void
  removeReport: (reportId: string) => void
  setCurrentReport: (report: Report | null) => void
  loadReports: (opts?: { q?: string; limit?: number; offset?: number; status?: string }) => Promise<void>
  setReportsQuery: (q: string) => void

  // Settings management
  updateSettings: (updates: Partial<Settings>) => void
  resetSettings: () => void
  // Reports pagination controls
  setReportsLimit: (limit: number) => void
  setReportsOffset: (offset: number) => void

  // Terminal management
  addTerminalOutput: (output: string) => void
  clearTerminalOutput: () => void
  addToHistory: (command: string) => void
  clearHistory: () => void

  // UI state management
  toggleSidebar: () => void
  setSidebarCollapsed: (collapsed: boolean) => void
  setActiveTerminalSession: (sessionId: string | null) => void
  // Terminal preferences
  setTerminalProfile: (profile: string) => void
  setTerminalCustomFlags: (flags: string) => void
  applyWafSuggestions: (flags?: string) => void
  updateTerminalForm: (updates: Partial<TerminalFormState>) => void
  resetTerminalForm: () => void
  setLastScanContext: (updates: Partial<LastScanContext>) => void
  resetLastScanContext: () => void

  // Loading state
  setLoading: (loading: boolean) => void

  // Report notifications
  enqueueReportNotification: (notification: ReportNotification) => void
  dismissReportNotification: (notificationId: string) => void

  // Scan governance
  setScanPolicy: (policy: ScanPolicyState | null, options?: { metaIncluded?: boolean }) => void
}

const defaultSettings: Settings = {
  theme: 'dark',
  terminalFontSize: 14,
  terminalTheme: 'default',
  notifications: true,
  autoSave: true,
  maxConcurrentScans: 3,
  defaultScanProfile: 'basic',
  apiTimeout: 30000,
}

const defaultTerminalForm: TerminalFormState = {
  targetUrl: '',
  authType: 'none',
  authCookie: '',
  authHeaderName: '',
  authHeaderValue: '',
  loginUrl: '',
  loginUsername: '',
  loginPassword: '',
  loginUsernameField: 'username',
  loginPasswordField: 'password',
  csrfRegex: '',
  csrfFieldName: '',
  csrfHeaderName: '',
  skipQueue: false,  // Default to queue-based precision scanning
}

const defaultLastScanContext: LastScanContext = {
  scanId: null,
  target: '',
  profile: 'basic',
  options: {},
  status: 'idle',
  startedAt: null,
  completedAt: null,
  exitCode: null,
  sessionId: null,
  error: null,
}

const defaultScanProfiles: ScanProfile[] = [
  {
    id: 'basic',
    name: 'Basic SQL Injection Scan',
    description: 'Quick scan for common SQL injection vulnerabilities',
    flags: ['--batch', '--random-agent', '--level=1', '--risk=1'],
  },
  {
    id: 'deep',
    name: 'Deep Scan',
    description: 'Comprehensive scan with higher risk and level',
    flags: ['--batch', '--random-agent', '--level=3', '--risk=2', '--threads=2'],
  },
  {
    id: 'aggressive',
    name: 'Aggressive Scan',
    description: 'Maximum detection with highest risk and level settings',
    flags: ['--batch', '--random-agent', '--level=5', '--risk=3', '--threads=1', '--forms', '--crawl=2', '--tamper=space2comment,charencode'],
  },
  {
    id: 'enumeration',
    name: 'Database Enumeration',
    description: 'Enumerate database structure and contents',
    flags: ['--batch', '--random-agent', '--dbs', '--tables', '--columns'],
  },
  {
    id: 'dump',
    name: 'Data Extraction',
    description: 'Extract data from vulnerable parameters',
    flags: ['--batch', '--random-agent', '--dump', '--exclude-sysdbs'],
  },
  {
    id: 'custom',
    name: 'Custom Scan',
    description: 'User-defined custom parameters',
    flags: [],
    baseMode: 'minimal',
  },
]

export type AppStore = AppState & AppActions

export const useAppStore = create<AppStore>()(
  devtools(
    persist(
      immer((set, get) => ({
        // Initial state
        isConnected: false,
        connectionError: null,
        authToken: null,
        currentUser: null,
        isAuthenticated: false,
        telemetryConsent: readTelemetryConsent(),
        isLoading: false,
        scanPolicy: null,
        scans: [],
        currentScan: null,
        scansTotal: 0,
        scanProfiles: defaultScanProfiles,
        reports: [],
        reportsTotal: 0,
        reportsLimit: 50,
        reportsOffset: 0,
        reportsQuery: '',
        currentReport: null,
        settings: defaultSettings,
        terminalHistory: [],
        terminalOutput: '',
        sidebarCollapsed: false,
        activeTerminalSession: null,
        terminalSelectedProfile: 'basic',
        terminalCustomFlags: '',
        lastCustomFlagsByUser: {},
        terminalForm: { ...defaultTerminalForm },
        lastScanContext: { ...defaultLastScanContext },
        reportNotifications: [],

        // Actions
        initialize: async () => {
          set((state) => {
            state.isLoading = true
          })

          abortInflightDataFetches()

          try {
            const response = await fetch('/api/health', { credentials: 'include' })
            if (response.ok) {
              set((state) => {
                state.isConnected = true
                state.connectionError = null
              })
            } else {
              throw new Error('Server health check failed')
            }

            const token = localStorage.getItem('authToken')
            if (token) {
              set((state) => {
                state.authToken = token
                state.isAuthenticated = true
              })
              try {
                await get().fetchMe({ suppressUnauthorizedEvent: true })
              } catch (error) {
                console.warn('Session restoration failed; clearing auth token', error)
                abortInflightDataFetches()
                localStorage.removeItem('authToken')
                set((state) => {
                  state.authToken = null
                  state.currentUser = null
                  state.isAuthenticated = false
                })
              }
            }

            if (get().isAuthenticated) {
              await Promise.allSettled([get().loadReports(), get().loadScans()])
            }
          } catch (error) {
            set((state) => {
              state.isConnected = false
              state.connectionError = error instanceof Error ? error.message : 'Unknown error'
            })
          } finally {
            set((state) => {
              state.isLoading = false
            })
          }
        },

        setConnected: (connected) => {
          set((state) => {
            state.isConnected = connected
            if (connected) {
              state.connectionError = null
            }
          })
        },

        setConnectionError: (error) => {
          set((state) => {
            state.connectionError = error
            if (error) {
              state.isConnected = false
            }
          })
        },

        setAuthToken: (token) => {
          set((state) => {
            state.authToken = token
            state.isAuthenticated = !!token
            if (token) localStorage.setItem('authToken', token)
            else localStorage.removeItem('authToken')
          })
        },

        setTelemetryConsent: (consent) => {
          set((state) => {
            state.telemetryConsent = consent
          })
          persistTelemetryConsent(consent)
        },

        fetchMe: async (opts?: { suppressUnauthorizedEvent?: boolean }) => {
          const me = await apiFetch<{ id: string; email: string; role: string }>('/api/auth/me', {
            suppressUnauthorizedEvent: opts?.suppressUnauthorizedEvent ?? false,
          })
          set((state) => {
            state.currentUser = me
            state.isAuthenticated = true
          })
        },

        login: async (email, password) => {
          const res = await apiFetch<{ token: string; user: { id: string; email: string; role: string } }>(
            '/api/auth/login',
            { method: 'POST', body: JSON.stringify({ email, password }) }
          )
          set((state) => {
            state.authToken = res.token
            state.currentUser = res.user
            state.isAuthenticated = true
          })
          localStorage.setItem('authToken', res.token)
        },

        register: async (email, password) => {
          const res = await apiFetch<{ token: string; user: { id: string; email: string; role: string } }>(
            '/api/auth/register',
            { method: 'POST', body: JSON.stringify({ email, password }) }
          )
          set((state) => {
            state.authToken = res.token
            state.currentUser = res.user
            state.isAuthenticated = true
          })
          localStorage.setItem('authToken', res.token)
        },

        logout: () => {
          abortInflightDataFetches()
          set((state) => {
            state.authToken = null
            state.currentUser = null
            state.isAuthenticated = false
          })
          localStorage.removeItem('authToken')
        },

        addScan: (scan) => {
          set((state) => {
            state.scans.unshift(scan)
          })
        },

        updateScan: (scanId, updates) => {
          set((state) => {
            const index = state.scans.findIndex(s => s.id === scanId)
            if (index !== -1) {
              Object.assign(state.scans[index], updates)
            }
            if (state.currentScan?.id === scanId) {
              Object.assign(state.currentScan, updates)
            }
          })
        },

        removeScan: (scanId) => {
          set((state) => {
            state.scans = state.scans.filter(s => s.id !== scanId)
            if (state.currentScan?.id === scanId) {
              state.currentScan = null
            }
          })
        },

        setCurrentScan: (scan) => {
          set((state) => {
            state.currentScan = scan
          })
        },

        loadScans: async () => {
          if (!get().isAuthenticated) {
            return
          }

          if (scansAbortController) {
            scansAbortController.abort()
          }
          const controller = new AbortController()
          scansAbortController = controller

          try {
            try {
              const metaRes = await apiFetch('/api/scans?limit=1', {
                signal: controller.signal,
              })
              if (metaRes && typeof metaRes === 'object') {
                const meta = (metaRes as any).meta || {}
                const total = typeof meta.total === 'number' ? meta.total : (typeof (metaRes as any).total === 'number' ? (metaRes as any).total : undefined)
                if (typeof total === 'number') {
                  set((state) => { state.scansTotal = total })
                }
              }
            } catch (e) {
              if (!isAbortError(e)) {
                console.debug('Meta fetch for /api/scans failed; continuing with primary fetch', e)
              } else {
                throw e
              }
            }

            const res = await apiFetch('/api/scans', {
              signal: controller.signal,
            })
            if (Array.isArray(res)) {
              set((state) => { state.scans = res; state.scansTotal = state.scansTotal || res.length })
            } else if (res && typeof res === 'object') {
              const items = Array.isArray((res as any).scans) ? (res as any).scans : (Array.isArray((res as any).items) ? (res as any).items : [])
              const meta = (res as any).meta || {}
              const total = typeof meta.total === 'number' ? meta.total : (typeof (res as any).total === 'number' ? (res as any).total : items.length)
              set((state) => { state.scans = items; state.scansTotal = total })
            } else {
              console.error('Unexpected response for /api/scans', res)
            }
          } catch (error) {
            if (!isAbortError(error)) {
              console.error('Failed to load scans:', error)
            }
          } finally {
            if (scansAbortController === controller) {
              scansAbortController = null
            }
          }
        },

        loadRunningScans: async () => {
          if (!get().isAuthenticated) {
            return
          }
          try {
            const scans = await apiFetch<Scan[]>('/api/scans/running')
            const activeRunning = scans.find((scan) => scan.status === 'running')
            set((state) => {
              const byId = new Map(state.scans.map(s => [s.id, s]))
              for (const s of scans) byId.set(s.id, { ...byId.get(s.id), ...s })
              state.scans = Array.from(byId.values()).sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))

              if (activeRunning) {
                const context = state.lastScanContext ? { ...state.lastScanContext } : { ...defaultLastScanContext }
                context.scanId = activeRunning.id
                context.target = activeRunning.target || context.target
                context.profile = activeRunning.scanProfile || context.profile || 'basic'
                context.options = activeRunning.options || context.options || {}
                context.status = activeRunning.status
                context.startedAt = activeRunning.startTime || context.startedAt || null
                context.completedAt = activeRunning.endTime || null
                context.exitCode = activeRunning.exitCode ?? null
                context.sessionId = (activeRunning as any).sessionId ?? context.sessionId ?? null
                context.error = activeRunning.error ?? null
                state.lastScanContext = context
              } else if (state.lastScanContext?.status === 'running') {
                state.lastScanContext = { ...state.lastScanContext, status: 'idle' }
              }
            })
          } catch (e) {
            console.error('Failed to load running scans:', e)
          }
        },

        upsertScanFromEvent: (scan) => {
          set((state) => {
            const idx = state.scans.findIndex(s => s.id === scan.id)
            if (idx === -1) {
              state.scans.unshift({
                id: scan.id,
                target: scan.target || '',
                options: scan.options || {},
                scanProfile: (scan as any).scanProfile || 'basic',
                status: (scan as any).status || 'running',
                startTime: (scan as any).startTime || new Date().toISOString(),
                endTime: undefined,
                exitCode: undefined,
                error: undefined,
                output: '',
                createdAt: (scan as any).createdAt || new Date().toISOString(),
                updatedAt: (scan as any).updatedAt || new Date().toISOString(),
                verdictMeta: (scan as any).verdictMeta ?? null,
              })
            } else {
              Object.assign(state.scans[idx], scan)
            }
          })
        },

        addReport: (report) => {
          set((state) => {
            state.reports.unshift(report)
          })
        },

        updateReport: (reportId, updates) => {
          set((state) => {
            const index = state.reports.findIndex(r => r.id === reportId)
            if (index !== -1) {
              Object.assign(state.reports[index], updates)
            }
            if (state.currentReport?.id === reportId) {
              Object.assign(state.currentReport, updates)
            }
          })
        },

        removeReport: (reportId) => {
          set((state) => {
            state.reports = state.reports.filter(r => r.id !== reportId)
            if (state.currentReport?.id === reportId) {
              state.currentReport = null
            }
          })
        },

        setCurrentReport: (report) => {
          set((state) => {
            state.currentReport = report
          })
        },

        loadReports: async (opts?: { q?: string; limit?: number; offset?: number; status?: string }) => {
          if (!get().isAuthenticated) {
            return
          }

          if (reportsAbortController) {
            reportsAbortController.abort()
          }
          const controller = new AbortController()
          reportsAbortController = controller

          try {
            const limit = typeof opts?.limit !== 'undefined' ? opts.limit : get().reportsLimit || 50
            const offset = typeof opts?.offset !== 'undefined' ? opts.offset : get().reportsOffset || 0
            const q = typeof opts?.q === 'string' ? opts.q : get().reportsQuery || ''
            const status = typeof opts?.status === 'string' ? opts.status : undefined
            const qp = `?limit=${encodeURIComponent(String(limit))}&offset=${encodeURIComponent(String(offset))}` +
              (q ? `&q=${encodeURIComponent(q)}` : '') + (status ? `&status=${encodeURIComponent(status)}` : '')
            const res = await apiFetch(`/api/reports${qp}`, {
              signal: controller.signal,
            })

            if (Array.isArray(res)) {
              set((state) => {
                state.reports = res
                state.reportsTotal = res.length
              })
            } else if (res && typeof res === 'object') {
              const reports = Array.isArray((res as any).reports) ? (res as any).reports : []
              const meta = (res as any).meta || {}
              const total = typeof meta.total === 'number' ? meta.total : (typeof (res as any).total === 'number' ? (res as any).total : reports.length)
              const limitFromServer = typeof meta.limit === 'number' ? meta.limit : get().reportsLimit
              const offsetFromServer = typeof meta.offset === 'number' ? meta.offset : get().reportsOffset
              set((state) => {
                state.reports = reports
                state.reportsTotal = total
                state.reportsLimit = limitFromServer
                state.reportsOffset = offsetFromServer
                state.reportsQuery = q
              })
            } else {
              console.error('Unexpected response for /api/reports', res)
            }
          } catch (error) {
            if (!isAbortError(error)) {
              console.error('Failed to load reports:', error)
            }
          } finally {
            if (reportsAbortController === controller) {
              reportsAbortController = null
            }
          }
        },

        setReportsQuery: (q: string) => {
          set((state) => {
            state.reportsQuery = q
            state.reportsOffset = 0
          })
          try {
            get().loadReports({ q, offset: 0 })
          } catch (e) {
            console.warn('loadReports refresh after query update failed', e)
          }
        },

        updateSettings: (updates) => {
          set((state) => {
            Object.assign(state.settings, updates)
          })
        },

        resetSettings: () => {
          set((state) => {
            state.settings = { ...defaultSettings }
          })
        },

        addTerminalOutput: (output) => {
          const MAX_BUFFER = 50000
          set((state) => {
            state.terminalOutput += output
            if (state.terminalOutput.length > MAX_BUFFER) {
              state.terminalOutput = state.terminalOutput.slice(-MAX_BUFFER)
            }
          })
        },

        clearTerminalOutput: () => {
          set((state) => {
            state.terminalOutput = ''
          })
        },

        addToHistory: (command) => {
          set((state) => {
            state.terminalHistory.push(command)
            // Keep only last 100 commands
            if (state.terminalHistory.length > 100) {
              state.terminalHistory = state.terminalHistory.slice(-100)
            }
          })
        },

        clearHistory: () => {
          set((state) => {
            state.terminalHistory = []
          })
        },

        toggleSidebar: () => {
          set((state) => {
            state.sidebarCollapsed = !state.sidebarCollapsed
          })
        },

        setSidebarCollapsed: (collapsed) => {
          set((state) => {
            state.sidebarCollapsed = collapsed
          })
        },

        setActiveTerminalSession: (sessionId) => {
          set((state) => {
            state.activeTerminalSession = sessionId
          })
        },

        // Reports pagination setters
        setReportsLimit: (limit: number) => {
          set((state) => {
            state.reportsLimit = limit
            state.reportsOffset = 0
          })
          // Reload reports with new limit
          try {
            get().loadReports()
          } catch (e) {
            console.warn('loadReports refresh after limit change failed', e)
          }
        },

        setReportsOffset: (offset: number) => {
          set((state) => {
            state.reportsOffset = offset
          })
          try {
            get().loadReports()
          } catch (e) {
            console.warn('loadReports refresh after offset change failed', e)
          }
        },

        // Terminal preferences
        setTerminalProfile: (profile) => {
          set((state) => {
            state.terminalSelectedProfile = profile
          })
        },
        setTerminalCustomFlags: (flags) => {
          set((state) => {
            state.terminalCustomFlags = flags
            const userId = state.currentUser?.id
            if (userId) {
              state.lastCustomFlagsByUser[userId] = flags
            }
          })
        },
        applyWafSuggestions: (flags) => {
          const suggested = flags && flags.trim().length > 0
            ? flags.trim()
            : '--tamper=space2comment,charencode,randomcase --threads=1 --delay=3'
          set((state) => {
            state.terminalSelectedProfile = 'custom'
            state.terminalCustomFlags = suggested
            const userId = state.currentUser?.id
            if (userId) {
              state.lastCustomFlagsByUser[userId] = suggested
            }
          })
        },

        updateTerminalForm: (updates) => {
          set((state) => {
            Object.assign(state.terminalForm, updates)
          })
        },

        resetTerminalForm: () => {
          set((state) => {
            state.terminalForm = { ...defaultTerminalForm }
          })
        },

        setLastScanContext: (updates) => {
          set((state) => {
            const base = state.lastScanContext ? { ...state.lastScanContext } : { ...defaultLastScanContext }
            const { options, ...rest } = updates
            Object.assign(base, rest)
            if (options !== undefined) {
              base.options = options
            }
            state.lastScanContext = base
          })
        },

        resetLastScanContext: () => {
          set((state) => {
            state.lastScanContext = { ...defaultLastScanContext }
          })
        },

        setLoading: (loading) => {
          set((state) => {
            state.isLoading = loading
          })
        },

        enqueueReportNotification: (notification) => {
          set((state) => {
            // Prevent duplicates for the same report in quick succession
            const exists = state.reportNotifications.some((n) => n.reportId === notification.reportId)
            if (!exists) {
              state.reportNotifications.unshift(notification)
              if (state.reportNotifications.length > 3) {
                state.reportNotifications = state.reportNotifications.slice(0, 3)
              }
            }
          })
        },

        dismissReportNotification: (notificationId) => {
          set((state) => {
            state.reportNotifications = state.reportNotifications.filter((n) => n.id !== notificationId)
          })
        },

        setScanPolicy: (policy, options = { metaIncluded: true }) => {
          set((state) => {
            if (!policy) {
              state.scanPolicy = null
              return
            }
            const metaIncluded = options.metaIncluded !== false
            const previous = state.scanPolicy
            state.scanPolicy = {
              allowArbitraryVerification: policy.allowArbitraryVerification ?? previous?.allowArbitraryVerification ?? true,
              enforcePreapprovedTargets: policy.enforcePreapprovedTargets ?? previous?.enforcePreapprovedTargets ?? false,
              disableNonAdminScans: policy.disableNonAdminScans ?? previous?.disableNonAdminScans ?? false,
              updatedAt: metaIncluded ? (policy.updatedAt ?? null) : previous?.updatedAt ?? null,
              updatedBy: metaIncluded ? (policy.updatedBy ?? null) : previous?.updatedBy ?? null,
            }
          })
        },
      })),
      {
        name: 'cybersecurity-app-store',
        partialize: (state) => ({
          settings: state.settings,
          terminalHistory: state.terminalHistory,
          sidebarCollapsed: state.sidebarCollapsed,
          authToken: state.authToken,
          currentUser: state.currentUser,
          terminalSelectedProfile: state.terminalSelectedProfile,
          terminalCustomFlags: state.terminalCustomFlags,
          lastCustomFlagsByUser: state.lastCustomFlagsByUser,
          terminalForm: state.terminalForm,
          lastScanContext: state.lastScanContext,
        }),
      }
    ),
    {
      name: 'cybersecurity-app',
      enabled: enableStoreDevtools,
    }
  )
)

const RUNNING_SCAN_STATUSES = new Set(['running'])
const QUEUED_SCAN_STATUSES = new Set(['pending', 'scheduled', 'queued', 'retrying'])
const ACTIVE_SCAN_STATUSES = new Set([...RUNNING_SCAN_STATUSES, ...QUEUED_SCAN_STATUSES])

const normalizeStatus = (status?: string | null) => (status ?? '').trim().toLowerCase()

export const isScanActive = (status?: string | null) => ACTIVE_SCAN_STATUSES.has(normalizeStatus(status))
export const isScanRunning = (status?: string | null) => RUNNING_SCAN_STATUSES.has(normalizeStatus(status))
export const isScanQueued = (status?: string | null) => QUEUED_SCAN_STATUSES.has(normalizeStatus(status))

export const selectActiveScans = (state: AppStore) => state.scans.filter((scan) => isScanActive(scan.status))
export const selectRunningScans = (state: AppStore) => state.scans.filter((scan) => isScanRunning(scan.status))
export const selectQueuedScans = (state: AppStore) => state.scans.filter((scan) => isScanQueued(scan.status))

export const selectActiveScanCount = (state: AppStore) => selectActiveScans(state).length
export const selectRunningScanCount = (state: AppStore) => selectRunningScans(state).length
export const selectQueuedScanCount = (state: AppStore) => selectQueuedScans(state).length
